/** @jsx jsx */
import React, { type SyntheticEvent } from 'react';
import { styled as styled2, css as cssCR, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import EditIcon from '@atlaskit/icon/core/edit';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { FieldDescription } from '@atlassian/jira-issue-field-description/src/ui/index.tsx';
import { FieldHeadingTitle } from '@atlassian/jira-issue-field-heading/src/styled.tsx';
import { ExpandCollapseToggle } from '@atlassian/jira-servicedesk-cmdb-expand-collapse-toggle/src/ui/index.tsx';
import type { Props } from './types.tsx';

export const CollapsibleFieldLabel = <T,>(props: Props<T>): JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		values,
		messages,
		isLoading,
		canEdit,
		additionalLabelContent: AdditionalLabelContent,
		issueKey,
		fieldId,
	} = props;

	const handleEditModeClick = (
		event: SyntheticEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		props.onEditModeClick(analyticsEvent);
	};

	const buttonLabel = formatMessage(props.isExpanded ? messages.hideDetails : messages.showDetails);

	const hasValues = values.length > 0;
	const shouldShowAddButton = !props.isEditing && !hasValues && !props.inlineErrorMessage;
	const shouldShowEditButton = !props.isEditing && hasValues && !props.inlineErrorMessage;
	const shouldShowLabelContent = shouldShowEditButton && !props.isExpanded;

	const editButton = (
		<Tooltip content={formatMessage(messages.editField)}>
			<Button
				appearance="subtle"
				iconBefore={
					<EditIcon
						spacing="spacious"
						label={formatMessage(messages.editField)}
						color={token('color.icon')}
					/>
				}
				onClick={handleEditModeClick}
				testId="issue-field-service-entity.common.ui.collapsible-field.label.edit-button"
				interactionName="auto-generated.issue-field-service-entity.common.ui.collapsible-field.label.edit-button.button1"
			/>
		</Tooltip>
	);

	return (
		<Wrapper>
			<div css={collapsibleFieldHeadingStyles}>
				<FieldHeadingTitle>{props.children}</FieldHeadingTitle>
				{issueKey !== undefined && fieldId !== undefined && (
					// @ts-expect-error label is required, please pass it through when working on this code.
					<FieldDescription issueKey={issueKey} fieldKey={fieldId} />
				)}
			</div>
			{props.inlineErrorMessage}
			{shouldShowLabelContent && AdditionalLabelContent && (
				<Box xcss={additionalLabelContentWrapperStyles}>
					<AdditionalLabelContent values={values} />
				</Box>
			)}
			{canEdit && shouldShowAddButton && !isLoading && (
				<Button
					appearance="link"
					iconBefore={<AddIcon label="" color={token('color.icon.brand')} />}
					onClick={handleEditModeClick}
					interactionName="auto-generated.jira-issue-field-service-entity.common.ui.collapsible-field.label.button"
				>
					{formatMessage(messages.addItem)}
				</Button>
			)}
			{shouldShowLabelContent && !isLoading && (
				<div css={objectCountStyles}>
					{formatMessage(messages.itemCount, { count: values.length })}{' '}
				</div>
			)}
			{canEdit && shouldShowEditButton && !isLoading && (
				<div
					css={[editButtonWrapperStyles, props.isParentHovered && editButtonWrapperVisibleStyles]}
				>
					{editButton}
				</div>
			)}
			{shouldShowEditButton && !isLoading && (
				<ExpandCollapseToggle
					isExpanded={props.isExpanded}
					label={buttonLabel}
					onClick={props.onExpandCollapseClick}
				/>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	minHeight: '32px',
});

const collapsibleFieldHeadingStyles = cssCR({
	display: 'flex',
	flexGrow: 1,
	marginRight: token('space.100'),
});

const additionalLabelContentWrapperStyles = xcss({
	marginRight: 'space.100',
});

const editButtonWrapperStyles = cssCR({
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		opacity: 0,
	},
});

const editButtonWrapperVisibleStyles = cssCR({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		opacity: 1.0,
	},
});

const objectCountStyles = cssCR({
	marginRight: token('space.100'),
});
